import React, { useCallback, useMemo, useRef } from 'react';
import { List } from 'tea-component';
import { ScrollBottomContext } from './context';
import type { TScrollBottomCallback } from './context';

export { useScrollBottomRegister, useScrollBottomContext } from './context';

interface IScrollBottomListProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * ScrollBottomList — wraps tea-component `<List>` at the page level
 * and provides Context so deep children can register their onScrollBottom callbacks.
 *
 * Usage:
 * ```tsx
 * // Page level
 * <ScrollBottomList>
 *   <Content>
 *     <DeepChild />
 *   </Content>
 * </ScrollBottomList>
 *
 * // Deep child
 * function DeepChild() {
 *   useScrollBottomRegister(() => loadNextPage());
 *   return <div>...</div>;
 * }
 * ```
 */
export default function ScrollBottomList({
  children,
  style,
  className,
}: IScrollBottomListProps) {
  const callbacksRef = useRef(new Set<React.MutableRefObject<TScrollBottomCallback | undefined>>());

  const register = useCallback((cbRef: React.MutableRefObject<TScrollBottomCallback | undefined>) => {
    callbacksRef.current.add(cbRef);
    return () => {
      callbacksRef.current.delete(cbRef);
    };
  }, []);

  const lastFireAtRef = useRef(0);

  const handleScrollBottom = useCallback(() => {
    if (callbacksRef.current.size === 0) return;
    // tea List 停留在底部时会连续触发；加冷却避免连点 nextPage → cancel 循环
    const now = Date.now();
    if (now - lastFireAtRef.current < 300) return;
    lastFireAtRef.current = now;
    callbacksRef.current.forEach((cbRef) => cbRef.current?.());
  }, []);

  const contextValue = useMemo(() => ({ register }), [register]);

  return (
    <ScrollBottomContext.Provider value={contextValue}>
      <List
        style={style || { overflow: 'auto' }}
        className={className}
        onScrollBottom={handleScrollBottom}
      >
        {children}
      </List>
    </ScrollBottomContext.Provider>
  );
}
